import React, { useEffect, useState, useRef, useCallback } from "react";
import classes from "./UpgradeBookAccount.module.css"
import AppHeader from "../Components/AppHeader";
import AppFooter from '../Components/AppFooter';
import Input from "../Components/Input";
import Toast from "../Components/Toast";
import { Link } from "react-router-dom/cjs/react-router-dom.min";
import { storedStringValue } from "../utils/localStorageString";
import { startBookUpgradeConfetti, stopBookUpgradeConfetti } from "../utils/bookUpgradeConfetti";
import { DICTIONARY_PAYMENT_RETURN_FLAG } from "../utils/dictionaryPaymentReturn";

const LINKED_BOOK_CODE_KEY = "linkedBookCode";

function langKey(appLanguage) {
    const l = String(appLanguage || '').toLowerCase();
    return l === 'en' || l === 'english' ? 'en' : 'de';
}

function isDictionaryPaymentSandbox() {
    const v = String(process.env.REACT_APP_DICTIONARY_PAYMENT_SANDBOX || '').toLowerCase();
    return v === 'true' || v === '1' || v === 'yes';
}

function dictionaryPaymentInitializePath() {
    return isDictionaryPaymentSandbox()
        ? '/v1/payment/dictionary/initialize/sandbox'
        : '/v1/payment/dictionary/initialize';
}

const COPY = {
    de: {
        back: "Zurück",
        bookLabel: "Buch-Account",
        bookTitle: "Aktivieren Sie Ihren digitalen Buch-Account!",
        bookSubtitle: "Bitte geben Sie den Code aus dem Buch ein",
        codePlaceholder: "Code",
        upgradeBtn: "Jetzt upgraden",
        unlinkBtn: "Buchcode entfernen",
        unlinkingBtn: "Lädt…",
        premiumLabel: "Premium-Account",
        premiumTitle: "Kein Buch? Dann abonnieren Sie die App",
        premiumPriceLabel: "Monatsabonnement",
        premiumPrice: "4.50 CHF",
        premiumPriceNote: "(jährliche Abrechnung)",
        subscribeBtn: "Abonnieren",
        subscribingBtn: "Lädt…",
        sandboxHint: "Nur zu Testzwecken – es erfolgt keine echte Abbuchung.",
        sandboxBtn: "Testzahlung (nicht real)",
        codeRequired: "Bitte geben Sie den Code ein",
        codeInvalid: "Ungültiger Code. Bitte versuchen Sie es erneut.",
        sessionExpired: "Sitzung abgelaufen. Bitte melden Sie sich erneut an.",
        unlinkFailed: "Der Buchcode konnte nicht entfernt werden.",
        unlinkedSuccess: "Der Buchcode wurde von Ihrem Konto getrennt.",
        paymentFailed: "Zahlung konnte nicht gestartet werden. Bitte versuchen Sie es erneut.",
        networkError: "Netzwerkfehler. Bitte versuchen Sie es erneut.",
        notSignedIn: "Bitte melden Sie sich an, um die Zahlung zu starten.",
    },
    en: {
        back: "Back",
        bookLabel: "Book account",
        bookTitle: "Activate your digital book account!",
        bookSubtitle: "Please enter the code provided in the book",
        codePlaceholder: "Code",
        upgradeBtn: "Upgrade now",
        unlinkBtn: "Unlink book code",
        unlinkingBtn: "Loading…",
        premiumLabel: "Premium account",
        premiumTitle: "Don't have the book? Then subscribe to the app",
        premiumPriceLabel: "Monthly subscription",
        premiumPrice: "4.50 CHF",
        premiumPriceNote: "(billing yearly)",
        subscribeBtn: "Subscribe",
        subscribingBtn: "Loading…",
        sandboxHint: "For testing only — no real payment will be charged.",
        sandboxBtn: "Test payment (not real)",
        codeRequired: "Please enter the code",
        codeInvalid: "Invalid code. Please try again.",
        sessionExpired: "Session expired. Please sign in again.",
        unlinkFailed: "Could not unlink the book code.",
        unlinkedSuccess: "The book code has been unlinked from your account.",
        paymentFailed: "Could not start payment. Please try again.",
        networkError: "Network error. Please try again.",
        notSignedIn: "Please sign in to start payment.",
    },
};

export default function UpgradeBookAccount(props) {
    const lang = langKey(props.language);
    const t = COPY[lang];

    const confettiCanvasRef = useRef(null);
    const playCelebrateSyncRef = useRef(false);

    const [mobile, setMobile] = useState(false);
    const [code, setCode] = useState('');
    const [codeError, setCodeError] = useState({ exists: false, helperText: null });
    const [enteredCodeTouched, setEnteredCodeTouched] = useState(false);
    const [comeFrom] = useState(window.localStorage.getItem("comeFrom"));
    const [codeValidated, setCodeValidated] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [releasing, setReleasing] = useState(false);
    const [paymentLoading, setPaymentLoading] = useState(false);

    const [toastVisible, setToastVisible] = useState(false);
    const [toastProperties, setToastProperties] = useState({});

    const showToast = useCallback((description, borderColor, icon, duration = 8000) => {
        setToastProperties({ description, borderColor, icon });
        setToastVisible(true);
        setTimeout(() => setToastVisible(false), duration);
    }, []);

    useEffect(() => {
        return () => stopBookUpgradeConfetti();
    }, []);

    // Return from payment gateway: go to success screen (toast + logout happen there).
    useEffect(() => {
        if (!sessionStorage.getItem(DICTIONARY_PAYMENT_RETURN_FLAG)) return;
        props.history.replace({ pathname: "/subscriptionSuccess" });
    }, [props.history]);

    useEffect(() => {
        if (!codeValidated || !playCelebrateSyncRef.current) return;
        playCelebrateSyncRef.current = false;
        let innerId;
        const outerId = requestAnimationFrame(() => {
            innerId = requestAnimationFrame(() => {
                startBookUpgradeConfetti(confettiCanvasRef.current);
            });
        });
        return () => {
            cancelAnimationFrame(outerId);
            if (innerId != null) cancelAnimationFrame(innerId);
        };
    }, [codeValidated]);

    useEffect(() => {
        const stored = window.localStorage.getItem(LINKED_BOOK_CODE_KEY);
        const acc = window.localStorage.getItem("account");
        if (stored && String(acc) === "2") {
            setCode(stored);
            setCodeValidated(true);
        }
    }, []);

    useEffect(() => {
        window.innerWidth < 900 ? setMobile(true) : setMobile(false);
    }, []);

    const handleChange = (evt) => {
        if (codeValidated) return;
        setCode(evt.target.value);
        setCodeError({ exists: false, helperText: null });
    };

    const userBlurHandler = () => setEnteredCodeTouched(true);

    const updateBookAccount = () => {
        if (submitting || codeValidated) return;
        const trimmed = code.trim();
        if (!trimmed) {
            setCodeError({ exists: true, helperText: t.codeRequired });
            setEnteredCodeTouched(true);
            return;
        }
        const accountInfo = {
            id: storedStringValue(window.localStorage.getItem("id")),
            token: storedStringValue(window.localStorage.getItem("jwt")),
            code: trimmed,
        };
        setSubmitting(true);
        fetch(`${process.env.REACT_APP_API_GOLANG_URL}/v1/validatebookcode/`, {
            method: "POST",
            body: JSON.stringify(accountInfo),
        })
            .then((response) => {
                if (response.status === 403) {
                    showToast(t.sessionExpired, "#f33757", "icon-warning", 15000);
                } else if (response.status === 401) {
                    setCodeError({ exists: true, helperText: t.codeInvalid });
                } else if (response.status === 200) {
                    playCelebrateSyncRef.current = true;
                    setCode(trimmed);
                    setCodeValidated(true);
                    setCodeError({ exists: false, helperText: null });
                    window.localStorage.setItem("account", 2);
                    window.localStorage.setItem(LINKED_BOOK_CODE_KEY, trimmed);
                }
                return response.json().catch(() => ({}));
            })
            .finally(() => setSubmitting(false));
    };

    const releaseBookCode = () => {
        if (releasing || !codeValidated || !code.trim()) return;
        const accountInfo = {
            id: storedStringValue(window.localStorage.getItem("id")),
            token: storedStringValue(window.localStorage.getItem("jwt")),
            code: code.trim(),
        };
        setReleasing(true);
        fetch(`${process.env.REACT_APP_API_GOLANG_URL}/v1/releasebookcode/`, {
            method: "POST",
            headers: { accept: "application/json", "Content-Type": "application/json" },
            body: JSON.stringify(accountInfo),
        })
            .then((response) => {
                if (response.status === 403) {
                    setReleasing(false);
                    showToast(t.sessionExpired, "#f33757", "icon-warning", 15000);
                    return;
                }
                if (response.status !== 200) {
                    setReleasing(false);
                    showToast(t.unlinkFailed, "#f33757", "icon-warning");
                    return;
                }
                showToast(t.unlinkedSuccess, "#8DD037", "icon-success");
                setTimeout(() => {
                    const rawId = window.localStorage.getItem("id");
                    fetch(`${process.env.REACT_APP_API_GOLANG_URL}/v1/removeactiveuser/${rawId}`, { method: "GET" });
                    window.localStorage.removeItem("jwt");
                    window.localStorage.removeItem("id");
                    window.localStorage.removeItem("account");
                    window.localStorage.removeItem("token");
                    window.localStorage.removeItem(LINKED_BOOK_CODE_KEY);
                    setReleasing(false);
                    setToastVisible(false);
                    props.history.push({ pathname: "/login" });
                }, 2500);
            })
            .catch(() => {
                setReleasing(false);
                showToast(t.unlinkFailed, "#f33757", "icon-warning");
            });
    };

    const handleDictionaryPayment = useCallback(async () => {
        const jwt = storedStringValue(window.localStorage.getItem('jwt'));
        const user_id = storedStringValue(window.localStorage.getItem('id'));
        if (!jwt || !user_id) {
            showToast(t.notSignedIn, "#f33757", "icon-warning");
            return;
        }
        setPaymentLoading(true);
        const apiBase = process.env.REACT_APP_API_GOLANG_URL || '';
        try {
            const res = await fetch(`${apiBase}${dictionaryPaymentInitializePath()}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_id, token: jwt, lang, recurring: true }),
            });
            const data = await res.json().catch(() => ({}));
            const redirect_url =
                data.redirect_url ||
                data.data?.redirect_url ||
                data.response?.redirect_url;
            if (res.ok && redirect_url) {
                sessionStorage.setItem(DICTIONARY_PAYMENT_RETURN_FLAG, "1");
                window.location.assign(redirect_url);
                return;
            }
            const msg =
                data.message ||
                data.error?.message ||
                data.error ||
                (typeof data === 'string' ? data : null) ||
                t.paymentFailed;
            showToast(String(msg), "#f33757", "icon-warning");
        } catch {
            showToast(t.networkError, "#f33757", "icon-warning");
        } finally {
            setPaymentLoading(false);
        }
    }, [lang, t, showToast]);

    return (
        <>
            <AppHeader
                currentPathName={props.location.pathname}
                mobile={mobile}
                comeFrom={comeFrom}
                bookDictionary={codeValidated}
                appLanguage={props.language}
            />
            <Toast
                toastList={toastProperties}
                position="top-right"
                visible={toastVisible}
                language={props.language}
            />
            <section className={classes.container}>
                <canvas ref={confettiCanvasRef} className={classes.confettiCanvas} aria-hidden />
                <div className={classes.pageWrap}>
                    <Link className={classes.backLink} to="/upgradeAccount">
                        <i className="icon icon-arrow" aria-hidden />
                        {t.back}
                    </Link>

                    <div className={classes.cardsRow}>
                        {/* ── Book account card ── */}
                        <div className={classes.card}>
                            <div className={classes.cardLabel}>
                                <i className="icon icon-book" aria-hidden />
                                <span>{t.bookLabel}</span>
                            </div>
                            <h2 className={classes.cardTitle}>{t.bookTitle}</h2>
                            <p className={classes.cardSubtitle}>{t.bookSubtitle}</p>

                            {codeValidated ? (
                                <div className={classes.linkedCodeWrap}>
                                    <span className={classes.linkedCode}>{code}</span>
                                    <button
                                        type="button"
                                        disabled={releasing}
                                        onClick={releaseBookCode}
                                        className="btn ochre"
                                    >
                                        {releasing ? t.unlinkingBtn : t.unlinkBtn}
                                    </button>
                                </div>
                            ) : (
                                <div className={classes.codeForm}>
                                    <Input
                                        title="Code"
                                        type="text"
                                        placeholder={t.codePlaceholder}
                                        value={code}
                                        handleChange={handleChange}
                                        handleBlur={userBlurHandler}
                                        className={codeError.exists ? "is-invalid" : ""}
                                        errorDiv={codeError.exists ? "text-danger" : "no-danger"}
                                        errorMsg={codeError.helperText}
                                    />
                                    <button
                                        type="button"
                                        disabled={submitting}
                                        onClick={updateBookAccount}
                                        className="btn ochre"
                                    >
                                        {submitting ? t.unlinkingBtn : t.upgradeBtn}
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className={classes.divider} aria-hidden />

                        {/* ── Premium account card ── */}
                        <div className={classes.card}>
                            <div className={classes.cardLabel}>
                                <i className="icon icon-premium" aria-hidden />
                                <span>{t.premiumLabel}</span>
                            </div>
                            <h2 className={classes.cardTitle}>{t.premiumTitle}</h2>

                            <div className={classes.priceBlock}>
                                <p className={classes.priceLabel}>{t.premiumPriceLabel}</p>
                                <p className={classes.price}>{t.premiumPrice}</p>
                                <p className={classes.priceNote}>{t.premiumPriceNote}</p>
                            </div>

                            {isDictionaryPaymentSandbox() && (
                                <p className={classes.sandboxHint}>{t.sandboxHint}</p>
                            )}

                            <button
                                type="button"
                                disabled={paymentLoading}
                                onClick={handleDictionaryPayment}
                                className="btn ochre"
                            >
                                {paymentLoading
                                    ? t.subscribingBtn
                                    : isDictionaryPaymentSandbox()
                                    ? t.sandboxBtn
                                    : t.subscribeBtn}
                            </button>
                        </div>
                    </div>
                </div>
            </section>
            <AppFooter language={props.language} />
        </>
    );
}
